/**
 * TimePickerModal.js
 *
 * FT-269: regulatory-grade event-time picker. Used for Let Go, All Fast,
 * Voyage Complete, in-voyage position log, weather log, and crew change
 * at sea. Decouples "when did this actually happen" (eventTime) from
 * "when did the Master click the button" (recordedAtDevice).
 *
 * Backdating bands (delta = now - enteredTime):
 *   < 30 min        → no friction, no reason required
 *   30 min .. 4h    → REQUIRED free-text reason (≥3 chars after trim)
 *   >= 4h           → REQUIRED structured dropdown + free-text on "Other"
 *
 * Future-time guard: native max= on date input + save-handler validation
 * with inline error.
 *
 * Threshold safety (per brief §24.9 reminder #1): the band detection is
 * the regulatory gatekeeper. Inline self-test below verifies the
 * boundary at 29min59s / 30min00s / 3h59min59s / 4h00min00s / 4h00min01s
 * so any future refactor that breaks the bands is loud about it.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { X, Clock, AlertTriangle } from 'lucide-react';
import {
  getOffsetForTimezone,
  getShortOffsetLabel,
  localTimeToUtcIso,
  formatDateInTimezone
} from '../../../../../utils/timezoneUtils';
// FT-284: single source of truth for the two boundary-override codes.
import { BOUNDARY_OVERRIDE_CODES } from '../../../../../utils/voyageBounds';

const THIRTY_MINUTES_MS = 30 * 60 * 1000;
const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;

/**
 * Compute the backdating band for a given delta in milliseconds.
 * Pure function — exported for unit-test access if needed.
 *   delta <  30min                  → 'none'
 *   30min <= delta <  4h            → 'free_text'
 *   delta >= 4h                     → 'structured'
 */
export function computeBackdatingBand(deltaMs) {
  if (!Number.isFinite(deltaMs) || deltaMs < THIRTY_MINUTES_MS) return 'none';
  if (deltaMs < FOUR_HOURS_MS) return 'free_text';
  return 'structured';
}

// === Inline boundary self-tests (FT-269 §24.9 reminder #1) ===========
// Run once on module load in development; throw if any boundary regresses.
// In production we don't throw — but the assertions still serve as
// documentation of the contract.
(function selfTestBoundaries() {
  const tests = [
    { delta: THIRTY_MINUTES_MS - 1000, expected: 'none', label: '29min59s' },
    { delta: THIRTY_MINUTES_MS,        expected: 'free_text', label: '30min00s' },
    { delta: FOUR_HOURS_MS - 1000,     expected: 'free_text', label: '3h59min59s' },
    { delta: FOUR_HOURS_MS,            expected: 'structured', label: '4h00min00s' },
    { delta: FOUR_HOURS_MS + 1000,     expected: 'structured', label: '4h00min01s' }
  ];
  for (const t of tests) {
    const got = computeBackdatingBand(t.delta);
    if (got !== t.expected) {
      const msg = `[TimePickerModal] FT-269 boundary regression at ${t.label}: expected ${t.expected}, got ${got}`;
      if (process.env.NODE_ENV !== 'production') {
        throw new Error(msg);
      } else {
        // eslint-disable-next-line no-console
        console.error(msg);
      }
    }
  }
})();

// Structured backdating reasons for the >=4h band (locked options per brief §6.1).
// FT-270 §17.3: two additional codes for the in-voyage boundary override path
// (events whose eventTime falls before voyage.departureTime or after
// voyage.arrivalTime). The modal itself remains voyage-agnostic — it just
// surfaces the codes. The boundary validation lives in voyageBounds.js (FT-284).
//
// FT-284: promoted to `export const` so useInlineEventTime can reuse the same
// codes without duplicating the array (no drift risk). The two override codes
// reference BOUNDARY_OVERRIDE_CODES by index so voyageBounds.js stays the
// single source of truth for them.
export const STRUCTURED_REASONS = [
  { code: 'tablet_unavailable', label: 'Tablet unavailable' },
  { code: 'engaged_with_operation', label: 'Engaged with operation' },
  { code: 'emergency_or_incident', label: 'Emergency or incident' },
  { code: 'post_incident_reconstruction', label: 'Post-incident reconstruction' },
  { code: BOUNDARY_OVERRIDE_CODES[0], label: 'Pre-departure observation (before Let Go)' },
  { code: BOUNDARY_OVERRIDE_CODES[1], label: 'Post-arrival log (after All Fast)' },
  { code: 'other', label: 'Other (specify)' }
];

function pad(n) { return String(n).padStart(2, '0'); }

/**
 * Build a `YYYY-MM-DD` / `HH:MM` pair representing "now in this timezone".
 * Used for initial values and the future-time guard.
 */
function nowInTimezone(timeZone) {
  try {
    const now = new Date();
    const dateStr = formatDateInTimezone(now.toISOString(), timeZone, { format: 'YYYY-MM-DD' });
    const timeStr = formatDateInTimezone(now.toISOString(), timeZone, { format: 'time' });
    return { dateStr, timeStr };
  } catch {
    const now = new Date();
    return {
      dateStr: `${now.getUTCFullYear()}-${pad(now.getUTCMonth() + 1)}-${pad(now.getUTCDate())}`,
      timeStr: `${pad(now.getUTCHours())}:${pad(now.getUTCMinutes())}`
    };
  }
}

const TimePickerModal = ({
  isOpen,
  onClose,
  title = 'Confirm Event',
  vesselTimezone = 'UTC',
  onConfirm
}) => {
  const tz = vesselTimezone || 'UTC';
  const [mode, setMode] = useState('current'); // 'current' | 'actual'
  const [localDate, setLocalDate] = useState('');
  const [localTime, setLocalTime] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [reasonOtherNote, setReasonOtherNote] = useState('');
  const [futureError, setFutureError] = useState(false);

  // Snapshot "now" once on open so the user can take their time without
  // the inputs drifting under them.
  useEffect(() => {
    if (!isOpen) return;
    const { dateStr, timeStr } = nowInTimezone(tz);
    setMode('current');
    setLocalDate(dateStr);
    setLocalTime(timeStr);
    setReasonText('');
    setReasonCode('');
    setReasonOtherNote('');
    setFutureError(false);
  }, [isOpen, tz]);

  // Compute the candidate event time (UTC) from the form inputs.
  const candidateEventTimeIso = useMemo(() => {
    if (mode === 'current') return new Date().toISOString();
    return localTimeToUtcIso(localDate, localTime, tz);
  }, [mode, localDate, localTime, tz]);

  // Delta between candidate event time and "right now".
  const deltaMs = useMemo(() => {
    return Date.now() - new Date(candidateEventTimeIso).getTime();
  }, [candidateEventTimeIso]);

  const band = useMemo(() => computeBackdatingBand(deltaMs), [deltaMs]);
  const isFuture = deltaMs < -30_000; // tolerate 30s clock skew
  const todayInTz = useMemo(() => nowInTimezone(tz).dateStr, [tz]);

  // Validation: when mode='actual', enforce future-time + band-specific reason.
  const reasonValid = (() => {
    if (mode === 'current') return true;
    if (isFuture) return false;
    if (band === 'none') return true;
    if (band === 'free_text') return reasonText.trim().length >= 3;
    if (band === 'structured') {
      if (!reasonCode) return false;
      if (reasonCode === 'other') return reasonOtherNote.trim().length >= 3;
      return true;
    }
    return false;
  })();

  if (!isOpen) return null;

  const handleConfirm = () => {
    // Future-time hard block (belt-and-braces, in addition to native max=).
    if (mode === 'actual' && isFuture) {
      setFutureError(true);
      return;
    }
    if (!reasonValid) return;

    const eventTime = candidateEventTimeIso;
    const eventTimezone = getOffsetForTimezone(tz, new Date(eventTime));
    let delayReason;
    if (mode === 'actual') {
      if (band === 'free_text') {
        delayReason = { code: 'free_text', note: reasonText.trim() };
      } else if (band === 'structured') {
        delayReason = reasonCode === 'other'
          ? { code: 'other', note: reasonOtherNote.trim() }
          : { code: reasonCode };
      }
    }
    onConfirm({ eventTime, eventTimezone, delayReason });
    onClose();
  };

  const offsetLabel = getShortOffsetLabel(tz);
  const utcHint = formatDateInTimezone(candidateEventTimeIso, 'UTC', { format: 'time' });

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-gray-900">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-500 rounded-lg">
              <Clock className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">{title}</h2>
              <p className="text-sm text-slate-300">{tz} {offsetLabel}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded-full transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-slate-300" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          {/* Mode selector */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMode('current')}
              className={`min-h-[56px] py-3 px-4 rounded-lg border-2 text-sm font-medium transition-all ${
                mode === 'current'
                  ? 'bg-cyan-600 border-cyan-700 text-white ring-2 ring-cyan-300'
                  : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >
              Use current time
            </button>
            <button
              type="button"
              onClick={() => setMode('actual')}
              className={`min-h-[56px] py-3 px-4 rounded-lg border-2 text-sm font-medium transition-all ${
                mode === 'actual'
                  ? 'bg-cyan-600 border-cyan-700 text-white ring-2 ring-cyan-300'
                  : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >
              Enter actual time
            </button>
          </div>

          {/* Actual-time inputs */}
          {mode === 'actual' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="ft269-event-date" className="block text-sm font-medium text-slate-700 mb-1">Date</label>
                  <input
                    id="ft269-event-date"
                    type="date"
                    value={localDate}
                    max={todayInTz}
                    onChange={(e) => { setLocalDate(e.target.value); setFutureError(false); }}
                    className="block w-full h-12 px-3 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="ft269-event-time" className="block text-sm font-medium text-slate-700 mb-1">Time (24h)</label>
                  <input
                    id="ft269-event-time"
                    type="time"
                    value={localTime}
                    onChange={(e) => { setLocalTime(e.target.value); setFutureError(false); }}
                    className="block w-full h-12 px-3 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                  />
                </div>
              </div>

              {/* Timezone hint (aria-live polite so screen readers announce it) */}
              <p className="text-xs text-slate-500" aria-live="polite">
                {tz} ({offsetLabel}). UTC equivalent: {utcHint}
              </p>

              {/* Future-time error */}
              {(isFuture || futureError) && (
                <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  Time cannot be in the future.
                </div>
              )}

              {/* Band-specific reason input */}
              {!isFuture && band === 'free_text' && (
                <div>
                  <label htmlFor="ft269-reason-text" className="block text-sm font-medium text-slate-700 mb-1">
                    Reason for delay <span className="text-red-500">(required)</span>
                  </label>
                  <input
                    id="ft269-reason-text"
                    type="text"
                    value={reasonText}
                    onChange={(e) => setReasonText(e.target.value)}
                    placeholder="Briefly explain why the time is in the past"
                    maxLength={500}
                    className="block w-full h-12 px-3 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                  />
                  {reasonText.trim().length > 0 && reasonText.trim().length < 3 && (
                    <p className="mt-1 text-xs text-red-600">Please enter a reason for the delay (at least 3 characters).</p>
                  )}
                </div>
              )}

              {!isFuture && band === 'structured' && (
                <div className="space-y-2">
                  <label htmlFor="ft269-reason-code" className="block text-sm font-medium text-slate-700">
                    Reason for delay <span className="text-red-500">(required)</span>
                  </label>
                  <select
                    id="ft269-reason-code"
                    value={reasonCode}
                    onChange={(e) => setReasonCode(e.target.value)}
                    className="block w-full h-12 px-3 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                  >
                    <option value="">Select a reason...</option>
                    {STRUCTURED_REASONS.map(r => (
                      <option key={r.code} value={r.code}>{r.label}</option>
                    ))}
                  </select>
                  {reasonCode === 'other' && (
                    <input
                      type="text"
                      value={reasonOtherNote}
                      onChange={(e) => setReasonOtherNote(e.target.value)}
                      placeholder="Specify..."
                      maxLength={500}
                      className="block w-full h-12 px-3 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-slate-200 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 min-h-[56px] py-3 px-4 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-100 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!reasonValid}
            className="flex-1 min-h-[56px] py-3 px-4 bg-cyan-600 text-white rounded-lg font-medium hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimePickerModal;
